// src/App.tsx — SCE Issues
// Point d'entrée applicatif : providers + routes

import { Fragment, Suspense, lazy, useCallback, useEffect, useLayoutEffect, useState, type ReactNode } from 'react'
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigationType,
  type Location,
} from 'react-router-dom'
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react'
import { AuthProvider } from '@/auth/AuthProvider'
import { useAuth } from '@/auth/useAuth'
import {
  deconnexionVolontaire,
  demarrerSession,
  type EtatSession,
  type MotifConnexion,
} from '@/auth/session'
import { attendreReseauCalme } from '@/api/activiteReseau'
import { SnackbarProvider } from '@/components/ui/Snackbar'
import { VueRoleProvider } from '@/features/vue-role/contexte'
import { PresenceProvider } from '@/components/presence/PresenceProvider'
import { ChargeurCouleursMetier } from '@/features/couleurs-metier/appliquer'
import { ChargeurRoles, useVersionRoles } from '@/features/roles/chargeur'
import { SignalementInvite } from '@/features/roles/signalementInvite'
import { ChercheurNcs } from '@/features/reference-ncs/ChercheurNcs'
import { ActeurNotifications } from '@/features/notifications-fnct/ActeurNotifications'
import { ChargeurCloche } from '@/features/cloche/ChargeurCloche'
import { VeilleChangements } from '@/features/rafraichissement/VeilleChangements'
import { BrouillonsNcs } from '@/components/issues/BrouillonsNcs'
import { EcranConnexion, OuvertureSession } from '@/components/connexion/EcranConnexion'
import { TirerPourRecharger } from '@/components/ui/TirerPourRecharger'
import { GardeComptePartage } from '@/components/connexion/QuiUtiliseLaTablette'
import { HomePage } from '@/pages/HomePage'
import { IssueListPage } from '@/pages/IssueListPage'
import { IssueDetailPage } from '@/pages/IssueDetailPage'
import { NewIssuePage } from '@/pages/NewIssuePage'
import { MyIssuesPage } from '@/pages/MyIssuesPage'
import { EquipementListPage } from '@/pages/EquipementListPage'
import { EquipementDetailPage } from '@/pages/EquipementDetailPage'
import { ProjetDetailPage } from '@/pages/ProjetDetailPage'
import { ChefProjetDetailPage } from '@/pages/ChefProjetDetailPage'
import { ModaleRoute } from '@/components/layout/ModaleRoute'
import { useEstBureau } from '@/hooks/useMediaQuery'

// ─── Pages chargées à la demande ───
//
// Le bundle tenait en un seul fichier d'environ 1 Mo, entièrement téléchargé
// avant le premier écran. Or l'utilisateur visé est un opérateur en 4G dans un
// atelier, et son parcours normal — déclarer un problème — ne touche jamais ces
// trois pages :
//
//  - /dashboard tire recharts et toute sa dépendance d3 (le plus gros morceau),
//  - /admin tire l'import Windchill et son parseur CSV,
//  - /debug n'existe même pas en production.
//
// Les sortir du chemin critique ne coûte qu'une attente d'un instant à qui les
// ouvre vraiment — et ces trois pages sont consultées au bureau, pas debout
// devant une machine.
const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
)
const AdminPage = lazy(() =>
  import('@/pages/AdminPage').then((m) => ({ default: m.AdminPage })),
)
const DebugPage = lazy(() =>
  import('@/pages/DebugPage').then((m) => ({ default: m.DebugPage })),
)
// Réglage ouvert aux coordinateurs et à l'administrateur, quelques fois par an.
const CouleursMetierPage = lazy(() =>
  import('@/pages/CouleursMetierPage').then((m) => ({ default: m.CouleursMetierPage })),
)
// FNC-T (chantier du 06/10) : administrateur seul pendant la construction.
const FnctListPage = lazy(() =>
  import('@/pages/FnctListPage').then((m) => ({ default: m.FnctListPage })),
)
const FnctDetailPage = lazy(() =>
  import('@/pages/FnctDetailPage').then((m) => ({ default: m.FnctDetailPage })),
)
// Réglé une fois, puis oublié (Lot 10 bis).
const NotificationsPage = lazy(() =>
  import('@/pages/NotificationsPage').then((m) => ({ default: m.NotificationsPage })),
)
// Ce que vous suivez (03/11) : sa page, où mène la cloche.
const SuivisPage = lazy(() => import('@/pages/SuivisPage').then((m) => ({ default: m.SuivisPage })))
const BEPage = lazy(() => import('@/pages/BEPage').then((m) => ({ default: m.BEPage })))
// Montage sous-traité (29/10) : coordinateurs, suppléants, administrateur.
const MontagesPage = lazy(() => import('@/pages/MontagesPage').then((m) => ({ default: m.MontagesPage })))

const DEV_BYPASS = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true'

/**
 * Composant racine.
 * Initialise MSAL, wrap les providers globaux (Auth + Snackbar) et
 * expose le router principal.
 */
export default function App(): JSX.Element {
  // ─── Bypass dev : contourne complètement MSAL ───
  if (DEV_BYPASS) {
    return (
      <SnackbarProvider>
        <BrowserRouter>
          <VueRoleProvider>
            <AppRoutes />
          </VueRoleProvider>
        </BrowserRouter>
      </SnackbarProvider>
    )
  }

  return <AppWithMsal />
}

/** Réseau calme depuis ce délai = la page d'arrivée a chargé ses listes. */
const CALME_RESEAU_MS = 400
/** Au-delà, le voile s'efface quoi qu'il arrive (accueil qui pagine longuement…). */
const PLAFOND_OUVERTURE_MS = 6000

function AppWithMsal(): JSX.Element {
  const [session, setSession] = useState<EtatSession | null>(null)
  const [msalError, setMsalError] = useState<string | null>(null)
  const [pagePrete, setPagePrete] = useState(false)
  const [voile, setVoile] = useState(true)
  const retirerVoile = useCallback(() => setVoile(false), [])

  // Initialisation MSAL + ouverture silencieuse de la session (Lot 10).
  useEffect(() => {
    demarrerSession()
      .then(setSession)
      .catch((err: unknown) => {
        setMsalError(err instanceof Error ? err.message : 'Erreur d\'initialisation MSAL')
        // eslint-disable-next-line no-console
        console.error('[App] MSAL init failed:', err)
      })
  }, [])

  // Le voile d'ouverture (Lot 11) couvre la page d'arrivée pendant qu'elle se
  // monte DESSOUS et charge ses listes. Cet effet s'exécute après ceux des
  // pages : leurs premières requêtes sont déjà parties quand on écoute.
  useEffect(() => {
    if (!session) return undefined
    if (!session.connecte) {
      setPagePrete(true)
      return undefined
    }
    let actif = true
    void attendreReseauCalme(CALME_RESEAU_MS, PLAFOND_OUVERTURE_MS).then(() => {
      if (actif) setPagePrete(true)
    })
    return () => {
      actif = false
    }
  }, [session])

  if (msalError) {
    return <FatalError message={msalError} />
  }

  return (
    <>
      {session && (
        <AuthProvider>
          <SnackbarProvider>
            <BrowserRouter>
              {session.connecte ? (
                <>
                  <AuthenticatedTemplate>
                    {/* Rôles du panneau d'administration (22/10) : lus une fois, sous MSAL. */}
                    <ChargeurRoles />
                    {/* Compte invité (iPad, téléphone perso) : se signale pour le panneau. */}
                    <SignalementInvite />
                    <AvecRoles>
                    {/* Appareil partagé (07/10) : « Qui utilise la tablette ? »
                        avant tout le reste. Sans effet pour les autres comptes. */}
                    <GardeComptePartage>
                    {/* Bascule de rôle (Lot 7) : sous l'AuthProvider, elle lit le
                        compte MSAL réel. La présence, elle, l'ignore — elle signale
                        toujours le vrai compte. */}
                    <VueRoleProvider>
                      <PresenceProvider>
                        {/* Couleurs métier (Lot 8) : lues une fois, sous MSAL. */}
                        <ChargeurCouleursMetier />
                        {/* N° de NC-S manquants, cherchés en fond à chaque liste chargée (06/10). */}
                        <ChercheurNcs />
                        {/* Notifications des FNC-T (F6, 23/10) : qui agit, pour ne pas se prévenir soi-même. */}
                        <ActeurNotifications />
                        {/* Cloche de l'en-tête (03/11) : lue, relue, et vidée de ce qu'on ouvre. */}
                        <ChargeurCloche />
                        {/* Listes à jour toutes seules (03/11) : relues quand quelqu'un a agi. */}
                        <VeilleChangements />
                        <AppRoutes />
                        {/* NC-S réduites, à reprendre depuis n'importe quelle page (06/10). */}
                        <BrouillonsNcs />
                      </PresenceProvider>
                    </VueRoleProvider>
                    </GardeComptePartage>
                    </AvecRoles>
                  </AuthenticatedTemplate>
                  <UnauthenticatedTemplate>
                    {/* Atteint après « Se déconnecter » : la déconnexion ne
                        quitte pas la page (auth/session.ts). */}
                    <LoginScreen motif={deconnexionVolontaire() ? 'deconnexion' : null} />
                  </UnauthenticatedTemplate>
                </>
              ) : (
                // Session non retrouvée — ou retour de connexion raté : on
                // l'affiche au lieu de relancer une redirection (boucle).
                <LoginScreen motif={session.motif} />
              )}
            </BrowserRouter>
          </SnackbarProvider>
        </AuthProvider>
      )}
      {voile && <OuvertureSession pret={pagePrete} onTermine={retirerVoile} />}
      {/* Téléphone : tirer la page vers le bas recharge toute l'application (09/10). */}
      <TirerPourRecharger />
    </>
  )
}

/**
 * Remonte l'appli quand la liste des rôles change (panneau d'administration) :
 * droits, accueil, menus et appareil partagé reprennent la nouvelle liste. Ne
 * se produit qu'au démarrage si SharePoint diffère de la copie locale, ou juste
 * après un enregistrement dans le panneau.
 */
function AvecRoles({ children }: { children: ReactNode }): JSX.Element {
  const version = useVersionRoles()
  return <Fragment key={version}>{children}</Fragment>
}

// ─── Routes protégées ───
//
// ─── Fiche en surimpression (bureau uniquement) ───
//
// Au-delà de 768px, une fiche s'affiche PAR-DESSUS une page de fond, qui reste
// montée : le défilement, la recherche et les filtres de la liste survivent à
// l'aller-retour.
//
// Le fond a deux origines :
//
//  1. **Navigation depuis une liste** — `useOuvrirFiche()` place la page de
//     départ dans `state.fond`.
//  2. **Lien direct** — mail Power Automate, favori, URL collée dans Teams, ou
//     simple F5 sur une fiche ouverte. Aucun état de navigation : on SYNTHÉTISE
//     la liste de rattachement. Même comportement que le lien « Gérer cette
//     action » d'Audit5S, qui ouvre le dashboard directement sur la modale.
//
// Sous 768px, les deux origines sont ignorées : la fiche redevient une page
// pleine, et surtout on ne monte JAMAIS deux pages en même temps.
//
// Le jeu de routes est déclaré UNE fois (`routes`) et réutilisé aux deux
// endroits : impossible qu'ils divergent.

/** Fiches pouvant s'ouvrir en surimpression, et leur liste de rattachement. */
const FICHES_MODALES = [
  { prefixe: '/equipements/', liste: '/equipements' },
  { prefixe: '/issues/', liste: '/issues' },
  // Projet et chef de projet se rattachent au référentiel : c'est la seule
  // liste où ces deux notions existent, et celle d'où l'on vient presque
  // toujours quand on ne suit pas un lien direct.
  { prefixe: '/projets/', liste: '/equipements' },
  { prefixe: '/chefs-projet/', liste: '/equipements' },
  { prefixe: '/fnct/', liste: '/fnct' },
] as const

/** Liste de rattachement d'une fiche, ou null si le chemin n'en est pas une. */
function listeDeRattachement(pathname: string): string | null {
  const f = FICHES_MODALES.find(
    (x) => pathname.startsWith(x.prefixe) && pathname.length > x.prefixe.length,
  )
  return f ? f.liste : null
}

function AppRoutes(): JSX.Element {
  const location = useLocation()
  const estBureau = useEstBureau()

  const fondExplicite = estBureau
    ? (location.state as { fond?: Location } | null)?.fond
    : undefined

  // Lien direct sur une fiche : on fabrique le fond manquant.
  const listeRattachement = estBureau ? listeDeRattachement(location.pathname) : null
  const fondSynthetique: Location | undefined =
    !fondExplicite && listeRattachement
      ? {
          pathname: listeRattachement,
          search: '',
          hash: '',
          state: null,
          key: 'fond-lien-direct',
        }
      : undefined

  const fond = fondExplicite ?? fondSynthetique

  // Une nouvelle page s'ouvre EN HAUT (23/10) : sans ça, au téléphone, la fiche
  // ouverte depuis une liste défilée gardait la hauteur de la liste (statut,
  // badges et cloche cachés sous l'en-tête). Pas au retour (Précédent : le
  // navigateur rend la position), ni quand une fiche s'ouvre PAR-DESSUS une
  // page au bureau (la page dessous garde son défilement), ni vers une ancre.
  const typeNavigation = useNavigationType()
  useLayoutEffect(() => {
    if (typeNavigation === 'POP' || fond || location.hash) return
    window.scrollTo(0, 0)
    // Sur un changement de PAGE seulement : un filtre écrit dans l'adresse
    // (?rubrique=…) ne doit pas faire remonter la page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  // Présent uniquement quand le fond est synthétisé : il n'y a alors pas
  // d'entrée d'historique derrière, donc fermer doit naviguer, pas reculer.
  const retourVers = fondSynthetique ? (listeRattachement ?? '/') : undefined

  const routes = (
    <Routes location={fond ?? location}>
      <Route path="/" element={<HomePage />} />
      <Route path="/issues" element={<IssueListPage />} />
      <Route path="/issues/:id" element={<IssueDetailPage />} />
      <Route path="/new" element={<NewIssuePage />} />
      <Route path="/my-issues" element={<MyIssuesPage />} />
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/equipements" element={<EquipementListPage />} />
      <Route path="/equipements/:numeroSerie" element={<EquipementDetailPage />} />
      <Route path="/projets/:pn" element={<ProjetDetailPage />} />
      <Route path="/chefs-projet/:nom" element={<ChefProjetDetailPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/fnct" element={<FnctListPage />} />
      <Route path="/fnct/:numero" element={<FnctDetailPage />} />
      <Route path="/couleurs-metiers" element={<CouleursMetierPage />} />
      <Route path="/notifications" element={<NotificationsPage />} />
      <Route path="/suivis" element={<SuivisPage />} />
      <Route path="/montages" element={<MontagesPage />} />
      {/* Pour le BE (24/10) : page simple en attendant leur cahier des charges. */}
      <Route path="/be" element={<BEPage />} />
      {/* Page debug — dev uniquement */}
      {import.meta.env.DEV && (
        <Route path="/debug" element={<DebugPage />} />
      )}
      {/* Fallback : redirection vers l'accueil */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )

  // Un seul Suspense pour les deux jeux de routes : une fiche en surimpression
  // n'est jamais paresseuse, seul le fond peut l'être (ouvrir une fiche
  // au-dessus du dashboard, par exemple).
  return (
    <Suspense fallback={<ChargementPage />}>
      {routes}
      {fond && (
        <Routes>
          <Route
            path="/equipements/:numeroSerie"
            element={
              <ModaleRoute fond={fond} retourVers={retourVers}>
                <EquipementDetailPage />
              </ModaleRoute>
            }
          />
          <Route
            path="/issues/:id"
            element={
              <ModaleRoute fond={fond} retourVers={retourVers}>
                <IssueDetailPage />
              </ModaleRoute>
            }
          />
          <Route
            path="/projets/:pn"
            element={
              <ModaleRoute fond={fond} retourVers={retourVers}>
                <ProjetDetailPage />
              </ModaleRoute>
            }
          />
          <Route
            path="/chefs-projet/:nom"
            element={
              <ModaleRoute fond={fond} retourVers={retourVers}>
                <ChefProjetDetailPage />
              </ModaleRoute>
            }
          />
          <Route
            path="/fnct/:numero"
            element={
              <ModaleRoute fond={fond} retourVers={retourVers} large>
                <FnctDetailPage />
              </ModaleRoute>
            }
          />
        </Routes>
      )}
    </Suspense>
  )
}

// ─── Attente de chargement d'une page paresseuse ───

/**
 * Volontairement sobre : sur une connexion correcte, ce voile ne s'affiche que
 * quelques dizaines de millisecondes. Un squelette détaillé y clignoterait plus
 * qu'il n'informerait.
 */
function ChargementPage(): JSX.Element {
  return (
    <div
      className="flex items-center justify-center"
      style={{ minHeight: '60vh' }}
      role="status"
      aria-label="Chargement de la page"
    >
      <div
        className="rounded-full animate-spin"
        style={{
          width: 32,
          height: 32,
          border: '3px solid var(--color-gray-20)',
          borderTopColor: 'var(--color-yellow)',
        }}
      />
    </div>
  )
}

// ─── Écran de connexion (Lot 11, maquette validée le 25/09) ───

function LoginScreen({ motif = null }: { motif?: MotifConnexion | null }): JSX.Element {
  const { login, error } = useAuth()
  // Un clic qui échoue avant même de partir chez Microsoft : même message
  // qu'un retour de connexion raté (le détail technique reste en console).
  return <EcranConnexion motif={error ? 'echec' : motif} onConnecter={() => void login()} />
}

// ─── Erreur fatale (MSAL KO) ───

function FatalError({ message }: { message: string }): JSX.Element {
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md text-center">
        <h1
          style={{
            fontSize: 'var(--font-size-h2)',
            fontWeight: 500,
            marginBottom: 8,
          }}
        >
          Application indisponible
        </h1>
        <p
          style={{
            fontSize: 'var(--font-size-copy2)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {message}
        </p>
      </div>
    </div>
  )
}
